import { AbstractClient, CompositionClient } from "@/app/client";
import { PageOptions } from "@/app/types";
import { UUID } from "crypto";

export interface EmailContactResponse {
  id: UUID;
  user_id: UUID;
  account_id: UUID;
  partner_id?: UUID | null;
  email_address: string;
  display_name?: string | null;

  // Conversation tracking fields
  last_message_subject?: string | null;
  last_message_content?: string | null;
  last_message_date?: string | null;
  last_message_direction?: string | null; // "inbound" or "outbound"
  unread_count: number;

  created_at: string;
  updated_at: string;
}

export interface EmailContactList {
  items: EmailContactResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface EmailsByContactResponse {
  contact: EmailContactResponse;
  emails: any[]; // Will be Email[] from existing types
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export default class APIEmailContactsSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  getEmailContacts = async (
    pageOptions?: PageOptions,
    accountId?: UUID,
  ): Promise<EmailContactList> => {
    const params: any = {};
    if (pageOptions) {
      params.page = pageOptions.page;
      params.page_size = pageOptions.size;
    }
    if (accountId) {
      params.account_id = accountId;
    }

    let res = await this._fetch({
      url: "/email-contacts/",
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getEmailContact = async (contactId: UUID): Promise<EmailContactResponse> => {
    let res = await this._fetch({
      url: `/email-contacts/${contactId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getContactEmails = async (
    contactId: UUID,
    pageOptions?: PageOptions,
  ): Promise<EmailsByContactResponse> => {
    const params: any = {};
    if (pageOptions) {
      params.page = pageOptions.page;
      params.page_size = pageOptions.size;
    }

    let res = await this._fetch({
      url: `/email-contacts/${contactId}/emails`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  markContactAsRead = async (
    contactId: UUID,
  ): Promise<EmailContactResponse> => {
    let res = await this._fetch({
      url: `/email-contacts/${contactId}/mark-read`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  linkPartnerToContact = async (
    contactId: UUID,
    partnerId: UUID,
  ): Promise<EmailContactResponse> => {
    let res = await this._fetch({
      url: `/email-contacts/${contactId}/link-partner`,
      method: "POST",
      body: { partner_id: partnerId },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  unlinkPartnerFromContact = async (
    contactId: UUID,
  ): Promise<EmailContactResponse> => {
    let res = await this._fetch({
      url: `/email-contacts/${contactId}/unlink-partner`,
      method: "POST",
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
